import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { Link, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { useToast } from "../../../../hooks/use-toast";
import { formatBytes, handleHttpError } from "../../../../utils/utils";

import { FormCsv } from "../../../../components/form-components/form-csv.components";
import { useCreateAssetCommonHttp } from "../../../../hooks/http/common/utils/asset/create-asset.common.http";
import {
  type ICSVUpload,
  useAddNewCsvFavoriteCollectionAdminHttp,
} from "../../../../hooks/http/admin/favorites/add-new-csv-favorite-collection.admin.http";
import { useEffect, useState } from "react";
import ArrowRightIcon from "../../../../assets/svgs/common/arrow-right.svg";
import DocumentIcon from "../../../../assets/svgs/common/document.svg";
import DownloadIcon from "../../../../assets/svgs/common/downlaod.svg";
import AddedCreatorIcon from "../../../../assets/svgs/common/added-creator.svg";
import FailedCreatorIcon from "../../../../assets/svgs/common/failed-creator.svg";
import millify from "millify";

const FormSchema = z.object({
  csv: z
    .object({
      id: z.string(),
      url: z.string(),
    })
    .nullable()
    .default(null),
  csvFile: z
    .array(
      z.object({
        file: z.instanceof(File),
      }),
    )
    .default([]),
});

export const ImportCsvModalAdminPartial: React.FunctionComponent = () => {
  const { favoriteId } = useParams();
  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadResult, setUploadResult] = useState<ICSVUpload>();
  const [assetCdnUrl, setAssetCdnUrl] = useState<string | null>(null);
  const {
    fetching: fetchingAddNewCsvFavoriteCollection,
    execute: addNewCsvFavoriteCollectionHttp,
  } = useAddNewCsvFavoriteCollectionAdminHttp();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  async function uploadAsset(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    setAssetCdnUrl(response.data.asset.url);
    return response.data.asset;
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    console.log(data);
    // if (!favoriteId) {
    // 	return;
    // }
  }

  async function addNewCsvFavoriteCollection() {
    if (!favoriteId) {
      return;
    }
    const response = await addNewCsvFavoriteCollectionHttp({
      body: {
        asset: form.getValues("csv")?.id ?? "",
        favouriteSocial: favoriteId,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "CSV file imported successfully",
      toastType: "success",
    });
    setUploadDone(true);
    setUploadResult(response.data.csvLog);
  }

  async function Upload() {
    const asset = await uploadAsset(form.getValues("csvFile")[0].file);
    if (!asset) {
      toast({
        content: "Failed to upload csv. Please try again",
        toastType: "error",
      });
      return;
    }
    form.setValue(
      "csv",
      {
        id: asset.id,
        url: asset.url,
      },
      { shouldDirty: true },
    );
    await addNewCsvFavoriteCollection();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.getValues("csvFile")?.length > 0) {
      console.log("submitting");
      Upload();
      // form.resetField("csvFile");
    }
  }, [form.watch("csvFile")]);

  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
      <DialogHeader className="w-full flex">
        <div className="flex flex-col">
          <p className="flex gap-1 font-semibold text-28 leading-8">
            Import csv file
          </p>
          <span className="text-secondary">
            Few final details before your import
          </span>
        </div>
      </DialogHeader>
      <div className="border-b border-border w-full" />
      <div className="flex items-center gap-4 w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex flex-col space-y-8 w-full"
          >
            {uploadDone ? (
              <div className="flex flex-col gap-8 p-5 w-full h-80">
                <div className="flex justify-between items-center p-3 border border-border border-dashed rounded-xl w-full">
                  <div className="flex items-center gap-8">
                    <DocumentIcon />
                    <div className="flex flex-col text-start">
                      <p className="font-semibold text-16">
                        {uploadResult?.title}
                      </p>
                      <p className="text-secondary text-xs">
                        {formatBytes(form.getValues("csvFile")[0].file.size)}
                      </p>
                    </div>
                  </div>
                  <Link to={assetCdnUrl ?? ""} target="_blank" className="">
                    <DownloadIcon />
                  </Link>
                </div>

                <div className="flex flex-col gap-8 p-3 font-semibold text-xl">
                  <p className="flex justify-between items-center">
                    <span className="flex items-center gap-4">
                      <AddedCreatorIcon /> Creators added
                    </span>{" "}
                    {millify(uploadResult?.successCount ?? 0)}
                  </p>
                  <p className="flex justify-between items-center">
                    <span className="flex items-center gap-4">
                      <FailedCreatorIcon /> Creators not added
                    </span>{" "}
                    {millify(uploadResult?.failedCount ?? 0)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <FormCsv
                  form={form}
                  name="csvFile"
                  label="Upload CSV"
                  uploadType="csv"
                  maxFiles={1}
                  multiple={false}
                  maxSize={100 * 1024 * 1024}
                  placeholder="Select a CSV file to import"
                  description="or drag and drop it here"
                  fetching={
                    uploadingAsset || fetchingAddNewCsvFavoriteCollection
                  }
                />
                <Button
                  className="bg-primarybuttonbg px-10 py-6 font-semibold text-black tracking-wider"
                  onClick={() => {
                    window.open(
                      "https://wldd-solo-assets-staging.s3.ap-south-1.amazonaws.com/2026/04/15/01KP8GH0GH6JQV0JSANXJ1E15Q/favourite%20groups_template.csv",
                      "_blank",
                    );
                  }}
                >
                  Download Template
                </Button>
              </div>
            )}

            {uploadDone && (
              <Link className="self-end" to={`/admin/favorites/${favoriteId}`}>
                <Button
                  type="button"
                  // disabled={uploadingAsset || fetchingAddNewCsvFavoriteCollection}
                  className="flex items-center bg-primarybuttonbg px-10 py-6 font-semibold text-black tracking-wider"
                >
                  Done
                  <span className="pt-1">
                    <ArrowRightIcon />
                  </span>
                </Button>
              </Link>
            )}
          </form>
        </Form>
      </div>
    </DialogContent>
  );
};
